import * as ko from 'knockout';

import i18n from '../i18n';
import { ListRequestParams } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import { ListLoaderDelegate, ListLoader, delegateActions } from '../components/list_loader';
import { formatAttributes } from '../models/attribute_meta';
import { DimensionMeta } from '../models/dimension_meta';
import * as dimensionMetasApi from '../api/dimension_metas';
import * as sitesApi from '../api/sites';
import { CountryData, countriesApi } from '../api/countries';
import * as namesApi from '../api/names';
import { NameI18nData } from '../api/names';
import { Site } from '../models/site';
import { ListHeaderAction } from '../components/list_header';
import { FilterDelegate } from '../components/list_filters';
import { deflateList } from '../api/serialization';
import { asArray, timeAgo, updateLocationWithQueryString } from '../utils';
import { Action } from '../components/basic_widgets';
import { DimensionRecordActions } from './dimension_records';
import {
  canCreateSite,
  canDeleteSite,
  canEditDimensionMeta,
  canEditSites,
  canImportDimensionRecord,
  reactListTablesEnabled,
} from '../permissions';
import { app } from '../app';
import { session } from '../session';
import { Deferred } from '../utils/deferred';
import { translate } from '../i18n_text';
import { ShowArchivedFilter } from '../components/show_archived_filter';
import * as regionsApi from '../api/regions';
import * as agroRegionsApi from '../api/agro_regions';
import { customersApi, partnersApi } from '../api/organizations';
import { GridChoices, listLookup, globalRegionsLookup } from '../utils/grid_choices';
import { GridView, refreshRows, pageParams } from '../utils/grid_view';
import { ARCHIVED_HIDDEN_FILTER } from '@/lib/gridViewState';
import {
  SitesListAttributeColumn,
  SitesListChoices,
  SitesListPage,
  SitesListProps,
  SitesListQuery,
  SitesListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/sites.html').default;

/** What one map can hold, rather than a page size: the map draws them all at once. */
const MAP_SITE_LIMIT = 2000;

class SitesScreen extends BaseLoadingScreen implements ListLoaderDelegate<sitesApi.SiteData, Site> {
  loader: ListLoader<sitesApi.SiteData, Site>;
  showArchivedFilter: ShowArchivedFilter;
  dimensionRecordActions: DimensionRecordActions;
  globalRegionsEnabled = !!session.tenant()?.global_regions_enabled;

  searchPlaceholder = i18n.t('Search sites by name')();
  private countryFilter = ko.observableArray<CountryData>(null);
  private globalRegionFilter = ko.observableArray<NameI18nData>(null);
  newFilters: FilterDelegate[] = [
    {
      title: i18n.t('Country')(),
      entities: this.countryFilter,
      list: countriesApi.list,
    },
    ...(this.globalRegionsEnabled
      ? [
          {
            title: i18n.t('Global region')(),
            entities: this.globalRegionFilter,
            list: (params: ListRequestParams) => namesApi.list('global_regions', params),
          },
        ]
      : []),
  ];

  canEdit = canEditSites();
  canCreate = canCreateSite();
  canDelete = canDeleteSite();
  listActions = ko.observableArray<ListHeaderAction>([]);

  dimensionMeta = ko.observable<DimensionMeta>(null);
  isLoading = ko.observable<boolean>(true);

  newSitesTable = reactListTablesEnabled();

  private view = new GridView(
    'sites',
    new GridChoices<SitesListChoices>(
      {
        country: listLookup(countriesApi.list, 'country'),
        region: listLookup(regionsApi.list, 'region'),
        agro_region: listLookup(agroRegionsApi.list, 'agroRegion'),
        global_region: globalRegionsLookup,
        partner: listLookup(partnersApi.list, 'partner'),
        customer: listLookup(customersApi.list, 'customer'),
      },
      () => ({
        country: [],
        region: [],
        agroRegion: [],
        globalRegion: [],
        partner: [],
        customer: [],
      })
    )
  );
  /** Shared with the legacy search box. */
  search = this.view.search;

  private attributeColumns = ko.pureComputed<SitesListAttributeColumn[]>(() => {
    const dimensionMeta = this.dimensionMeta();
    if (!dimensionMeta) {
      return [];
    }
    return dimensionMeta.attributeHeaders();
  });

  tableProps = ko.pureComputed<SitesListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    globalRegionsEnabled: this.globalRegionsEnabled,
    attributeColumns: this.attributeColumns(),
  }));

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: {
    filters: { country_ids: string | string[]; global_region_ids: string | string[]; search: string };
  }) {
    super();

    let countryIds = asArray(params.filters.country_ids);
    let countriesPromise =
      countryIds.length > 0 ? countriesApi.list({ ids: countryIds }) : Promise.resolve<CountryData[]>([]);

    let globalRegionIds = asArray(params.filters.global_region_ids);
    let globalRegionsPromise =
      globalRegionIds.length > 0 && this.globalRegionsEnabled
        ? namesApi.list('global_regions', { ids: globalRegionIds })
        : Promise.resolve<NameI18nData[]>([]);

    if (params.filters.search) {
      this.search(params.filters.search);
    }

    let tableLoaded = Promise.resolve();
    if (this.newSitesTable) {
      tableLoaded = this.view.load(params.filters, {
        firstVisit: { filter: ARCHIVED_HIDDEN_FILTER },
      });
    }
    this.showArchivedFilter = new ShowArchivedFilter(dimensionMetasApi.SITE_SLUG, () => {
      if (this.loader) this.loader.forceLoad();
    });

    this.dimensionRecordActions = new DimensionRecordActions(() => this.canEdit, {
      onArchive: () => this.applyChange(),
      onUnarchive: () => this.applyChange(),
    });

    let dmPromise = dimensionMetasApi.retrieve(dimensionMetasApi.SITE_SLUG);
    let promise = Promise.all([countriesPromise, globalRegionsPromise, dmPromise])
      .then(([countries, globalRegions, dmData]) => {
        if (countries) {
          this.countryFilter(countries);
        }
        if (globalRegions) {
          this.globalRegionFilter(globalRegions);
        }
        this.dimensionMeta(new DimensionMeta(dmData));
      })
      .then(() => {
        if (!this.newSitesTable) {
          return;
        }
        // The table shows its own loading, so the Map action stops waiting; the island mounts once
        // the filter's labels resolve.
        this.isLoading(false);
        return tableLoaded;
      });
    this.loadedAfter(promise);

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/sites/new/',
        tooltipTitle: i18n.t('Add site')(),
      });
    }
    // Customize and Import target the site dimension's endpoints, so they gate on the dimension
    // codenames the backend enforces there rather than the site codename.
    if (canEditDimensionMeta()) {
      this.listActions.push({
        title: i18n.t('Customize')(),
        icon: 'settings',
        href: '/dimensions/site/',
      });
    }
    if (canImportDimensionRecord()) {
      this.listActions.push({
        title: i18n.t('Import')(),
        icon: 'file_upload',
        href: '/dimensions/site/import/',
        tooltipTitle: i18n.t('Import sites')(),
      });
    }
    this.listActions.push({
      title: i18n.t('Map')(),
      icon: 'map',
      loading: this.isLoading,
      onClick: () => this.openMap(),
      tooltipTitle: i18n.t('Show filtered sites on a map')(),
    });
  }

  /** The table holds one page, so the query is re-issued. */
  openMap() {
    if (!this.newSitesTable) {
      this.openMapFor(this.loader.items());
      return;
    }

    this.isLoading(true);
    sitesApi
      .list({
        offset: 0,
        limit: MAP_SITE_LIMIT,
        ...this.view.queryParams('search'),
        // Archived is a filter condition, so `filter` alone decides which sites the map shows.
        archived: null,
      })
      .then((data) => {
        this.openMapFor(data.map((item) => new Site(item, this.dimensionMeta())));
      })
      .catch(() => {
        // The api layer reports the failure; this only has to stop the action spinning.
      })
      .finally(() => {
        this.isLoading(false);
      });
  }

  private openMapFor(sites: Site[]) {
    const locations = sites
      .filter((site) => site.gpsLocation())
      .map((site) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [site.gpsLocation().lng, site.gpsLocation().lat],
          },
          properties: {
            info: [{ title: i18n.t('Site')(), value: translate(site.nameJson()) }],
            title: i18n.t('Site')(),
            iconType: 'site',
          },
        };
      });
    const shapes = sites
      .filter((site) => site.siteArea())
      .map((site) => {
        return {
          type: 'Feature',
          geometry: site.siteArea().geometry,
          properties: {
            info: [{ title: i18n.t('Name')(), value: translate(site.nameJson()) }],
            title: i18n.t('Site shape')(),
            iconType: 'site',
          },
        };
      });

    app.formsStackController.push({
      title: i18n.t('Sites')(),
      name: 'map',
      isBig: true,
      params: {
        value: [...locations, ...shapes],
        result: new Deferred<{}>(),
      },
    });
  }

  openMapForSite(site: Site) {
    const locations = site.gpsLocation()
      ? [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [site.gpsLocation().lng, site.gpsLocation().lat],
            },
            properties: {
              info: [{ title: i18n.t('Site')(), value: translate(site.nameJson()) }],
              title: i18n.t('Site')(),
              iconType: 'site',
            },
          },
        ]
      : [];

    const shapes = site.siteArea()
      ? [
          {
            type: 'Feature',
            geometry: site.siteArea().geometry,
            properties: {
              info: [{ title: i18n.t('Name')(), value: translate(site.nameJson()) }],
              title: i18n.t('Site shape')(),
              iconType: 'site',
            },
          },
        ]
      : [];

    app.formsStackController.push({
      title: i18n.t('Site Map')(),
      name: 'map',
      isBig: true,
      params: {
        value: [...locations, ...shapes],
        result: new Deferred<{}>(),
      },
    });
  }

  onReady(loader: ListLoader<sitesApi.SiteData, Site>) {
    this.loader = loader;
    this.subscriptions.push(this.search.subscribe(() => this.loader.forceLoad()));
    this.subscriptions.push(
      this.loader.loading.subscribe((loading) => {
        this.isLoading(loading);
      })
    );
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }

  fetch(params: ListRequestParams) {
    let filters = this.getFilters();
    updateLocationWithQueryString(filters);

    return sitesApi.list({ ...filters, ...params });
  }

  instantiate(siteData: sitesApi.SiteData) {
    return new Site(siteData, this.dimensionMeta());
  }

  remove(id: string) {
    return sitesApi.remove(id);
  }

  canRemove(site: Site) {
    return this.canDelete;
  }

  private getFilters() {
    return {
      country_ids: deflateList(this.countryFilter),
      global_region_ids: deflateList(this.globalRegionFilter),
      search: this.search(),
      ...(this.showArchivedFilter.on() ? { archived: null } : { archived: false }),
    };
  }

  /** Fetches one page for the table, and builds its rows and their actions. */
  private fetchPage = (query: SitesListQuery): Promise<SitesListPage> =>
    this.view.fetchPage(
      query,
      (options) =>
        sitesApi.list(
          {
            ...pageParams(query, 'search'),
            // Archived is a filter condition; `archived: false` as well would AND the two to nothing.
            archived: null,
          },
          {},
          options
        ),
      this
    );

  toRow = (site: Site, data: sitesApi.SiteData): SitesListRow => {
    return {
      id: site.id(),
      name: translate(site.nameJson()),
      url: session.toTenantPath(site.editUrl()),
      countryName: site.country() ? site.country().name : '',
      regionName: site.region() ? translate(site.region().name_json) : '',
      agroRegionName: site.agroRegion() ? translate(site.agroRegion().name_json) : '',
      globalRegionName:
        site.country() && site.country().global_region
          ? translate(site.country().global_region.name_json)
          : '',
      partnerName: site.partner() ? site.partner().name : '',
      customerName: site.customer() ? site.customer().name : '',
      code: site.code() || '',
      modified: data.modified ? timeAgo(data.modified) : '',
      archived: !!site.archived(),
      attributes: formatAttributes(site.attributes()),
      actions: delegateActions(this, site, this.view.reload),
    };
  };

  private applyChange = () => refreshRows(this.newSitesTable, this.view, this.loader);

  getActions(site: Site): Action[] {
    const defaultActions: Action[] = this.dimensionRecordActions.getAll({
      id: site.id(),
      dimensionMetaNameSlug: this.dimensionMeta().nameSlug(),
      archived: site.archived(),
    });

    if (site.gpsLocation() || site.siteArea()) {
      defaultActions.push({
        icon: 'map',
        title: i18n.t('View on map')(),
        cssClass: '',
        onClick: () => this.openMapForSite(site),
      });
    }
    return defaultActions;
  }
}

export let sites = {
  name: 'sites',
  viewModel: SitesScreen,
  template: template,
};

ko.components.register(sites.name, sites);
